import { useEffect, useRef } from "react";
import { willTokenExpireWithin } from "@maeiz/core";
import { authApi } from "@api/modules/auth.api";
import { useAuthStore } from "@stores/auth/authStore";

const REFRESH_WHEN_WITHIN_SECONDS = 5 * 60;
const CHECK_INTERVAL_MS = 60 * 1000;

/**
 * Keeps the access token fresh while the console is open: on mount, on an interval and when
 * the tab regains focus it refreshes once the token is within 5 minutes of expiry. It never
 * ends the session itself — the server is the authority (an expired token gets a 401, which
 * the http client turns into a refresh-or-sign-out).
 */
export const useAuthRefresh = () => {
    const inFlight = useRef(false);

    useEffect(() => {
        const maybeRefresh = async () => {
            const { token, user, isAuthenticated } = useAuthStore.getState();
            if (!token || !user || !isAuthenticated || inFlight.current) {
                return;
            }
            if (!willTokenExpireWithin(token, REFRESH_WHEN_WITHIN_SECONDS)) {
                return;
            }
            inFlight.current = true;
            try {
                const res = await authApi.refreshToken();
                const next = res.accessToken || res.token;
                if (next) {
                    useAuthStore.getState().setAuth(user, next, { billingOnly: res.billingOnly === true });
                }
            } catch {
                /* a 401 here is handled by the response interceptor */
            } finally {
                inFlight.current = false;
            }
        };

        void maybeRefresh();
        const interval = window.setInterval(() => void maybeRefresh(), CHECK_INTERVAL_MS);
        const onVisible = () => {
            if (document.visibilityState === "visible") {
                void maybeRefresh();
            }
        };
        window.addEventListener("focus", onVisible);
        document.addEventListener("visibilitychange", onVisible);
        return () => {
            window.clearInterval(interval);
            window.removeEventListener("focus", onVisible);
            document.removeEventListener("visibilitychange", onVisible);
        };
    }, []);
};
